import React, { useEffect, useRef } from "react";
import { UIComponents } from "@/components/ui";
import { SharedNotificationDropdownProps } from "./type";

const SharedNotificationDropdown = <TItem extends object, >({
  isOpen,
  onClose,
  title,
  emptyMessage,
  items,
  getItemKey,
  onItemClick,
  renderItem,
  onViewAll,
}: SharedNotificationDropdownProps<TItem>): React.ReactElement => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect((): (() => void) => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("click", handleClickOutside);
    }

    return (): void => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return <></>;
  }

  return (
    <div
      ref={dropdownRef}
      className="fixed top-16 left-1/2 z-50 w-[calc(100%-32px)] max-w-80 -translate-x-1/2 rounded-20 border border-border bg-white p-4 shadow-[-4px_8px_36px_rgba(17,17,17,0.2)] md:absolute md:top-full md:right-0 md:left-auto md:mt-2 md:w-80 md:max-w-none md:translate-x-0"
    >
      <h3 className="text-base font-semibold text-foreground mb-3">{title}</h3>

      <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            {emptyMessage}
          </p>
        ) : (
          items.map((item) => (
            <UIComponents.button
              key={getItemKey(item)}
              type="button"
              onClick={() => onItemClick(item)}
              variant="ghost"
              className="!w-full !h-auto !p-3 rounded-12 bg-bg-hover hover:bg-background transition-colors text-left flex-col items-start min-w-0"
            >
              {renderItem(item)}
            </UIComponents.button>
          ))
        )}
      </div>

      <UIComponents.button
        type="button"
        onClick={onViewAll}
        className="w-full justify-center"
      >
        View All
      </UIComponents.button>
    </div>
  );
};

export default SharedNotificationDropdown;
